import { roundDecimalPlaces, distancePt } from './utils.js'

// This class deals with filling/expanding shapes 
// based on their parameters and some arguments. 
// Will probably be most aimed at circles but can see what else it might 
// work for
class ShapeFillRandom {
    constructor(
        shape,
        centerPt
    ) {
        // Each shape much have a defined center point (.cx, .cy) and 
        // have a getCoordinates() method for this to work.  
        this.shape = shape
        this.centerPt = centerPt
    }

    // The curve points are a 3 dim array to account for being 
    // different for each input shape, while the params will be the same 
    // for each of them
    getRandomPoints(endsParams, args) {
        let curvePoints = []
        let distance = 1
        for (let params of endsParams) {
            curvePoints.push([])
            for (let i = 0; i < params.length - 1; i+=2) {
                curvePoints.at(-1).push(distancePt(this.shape, this.centerPt, params.at(i), distance))
                curvePoints.at(-1).push(distancePt(this.shape, this.centerPt,params.at(i + 1), distance))
                curvePoints.at(-1).push(distancePt(this.shape, this.centerPt,params.at(i + 1), distance + args.thickness))
                curvePoints.at(-1).push(distancePt(this.shape, this.centerPt,params.at(i), distance + args.thickness))
            }
            distance += args.thickness + args.spacing
        }
        return curvePoints
    }

    getCenterParamsRandom(sizes) {
        let centers = []
        let startLocation = Math.random()
        let pad = (1 - sizes.reduce((a, b) => a + b)) / sizes.length
        let adjustedStart = startLocation
        for (let size of sizes) {
            adjustedStart = (adjustedStart + (size / 2) + (pad / 2))
            centers.push(roundDecimalPlaces(adjustedStart, 5))
            adjustedStart = (adjustedStart + (size / 2) + (pad / 2))
        }
        return centers
    }


    randomInRange(min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min
    } 

    // Size factors indicates how different the size for each curve
    // can be. The range gets normalized but it indicates the relative
    // difference
    randomSizes(curves, coverage, args) {
        let valueArr = []
        for (let i = 0; i < curves; i++) {
            valueArr.push(this.randomInRange(...args.sizeFactors))
        }
        coverage = coverage === 0 ? Math.min(...args.sizeFactors) / Math.max(...args.sizeFactors) : coverage
        let total = valueArr.reduce((a, b) => a + b) * (1 / coverage)
        return valueArr.map(val => roundDecimalPlaces(val / total, 3))
    } 


    getRandomizedEndParams(args) {
        let centerParams = []
        let endParams = []
        // Use a reduction factor from the initial, not from the 
        // current since that will end up being asymptotic
        for (let i = 0; i < args.layers; i++) {
            let curveCount = this.randomInRange(...args.curveRange)
            let change = 1 + args.change * i
            let coverage = args.coverageRange.length === 2 
                ? (this.randomInRange(...args.coverageRange) / Math.max(...args.coverageRange)) * args.coverage 
                : args.coverage
            if (coverage * change > 1) {
                change = 1 / coverage
            }
            let sizes = this.randomSizes(curveCount, coverage, args)
            endParams.push([])
            centerParams.push(this.getCenterParamsRandom(sizes))
            let n = 0
            for (let centerParam of centerParams.at(-1)) {
                let halfSize = sizes.at(n) * change / 2
                let start = centerParam - halfSize < 0 ? centerParam + 1 - halfSize : centerParam - halfSize
                let end = centerParam + halfSize
                // If disjoint protection is in place, make sure we DONT
                // end up with values crossing over the start/end 
                // of the shape, eg 0.95 to 1.03 or 1.98 to 2.11
                if (args.disjointProtection) {

                    let [boundStart, boundEnd] = this.calculateIndexBounds(start, end)
                    endParams.at(i).push(roundDecimalPlaces(boundStart, 5))
                    endParams.at(i).push(roundDecimalPlaces(boundEnd, 5))
                } else {
                    endParams.at(i).push(roundDecimalPlaces(start, 5))
                    endParams.at(i).push(roundDecimalPlaces(end, 5))
                }
                n++
            }
        }
        return [centerParams, endParams]
    }



    // The purpose of this is to prevent arcs from overflowing 
    // when a series starts and ends at different points
    // is not neat/complete. 
    // instead of going from 0.9 to 1.1 and causing a problem, it will cap 
    // the second value at 1.0.
    calculateIndexBounds(i, i2) {
        i = i % 1
        i2 = i2 % 1
        if (i2 < 0.25 && i > 0.75) {
            if (i2 > (1 - i)) {
                i = 0.001
            } else {
                i2 = 0.999
            }
        }
        if (i < 0.25 && i2 > 0.75) {
            if (i > (1 - i2)) {
                i2 = 0.001
            } else {
                i = 0.999
            }
        }
        return [i, i2]
    }


    buildCurve(shape, pointArr, params, sizeArr, args) {
        let path = ``
        let [startInnerPt, endInnerPt, startOuterPt, endOuterPt] = pointArr
        let [sizeInner, sizeOuter] = sizeArr

        // Go to start
        path = `${path} M ${startInnerPt.x} ${startInnerPt.y} `

        // draw to inner arc end
        path = args.connectPoints ? `${path} L ${endInnerPt.x} ${endInnerPt.y} ` : `${path} ${shape.getSubCurveRelative(...params, sizeInner, args.resolution)} `

        // line to outer arc start
        path = `${path} L ${startOuterPt.x} ${startOuterPt.y} `

        // draw outer arc
        path = args.connectPoints ? `${path} L ${endOuterPt.x} ${endOuterPt.y} ` : `${path} ${shape.getSubCurveRelative(...params.reverse(), sizeOuter, args.resolution)} `

        // line back to start point
        path = `${path} L ${startInnerPt.x} ${startInnerPt.y} `
        return path
    }


    getRandomShapes(curvePoints, endParams, args) {
        let paths = []
        let n = 0
        let thickness = args.thickness
        for (let pointSet of curvePoints) {
            let curveParams = endParams.at(n)
            let sizeInner = 1 + (n * (thickness + args.spacing))
            let sizeOuter = 1 + ((n + 1) * thickness + (n * args.spacing))
            // console.log(`Input random points: ${JSON.stringify(pointSet)}`)
            for (let i = 0; i < pointSet.length - 3; i += 4) {
                let params = curveParams.slice(i / 2, i / 2 + 2)
                let points = pointSet.slice(i, i + 4)
                paths.push(this.buildCurve(this.shape, points, params, [sizeInner, sizeOuter], args))
            }
            n++
        }
        return paths
    }


    fillShapeRandomArcs(args) {
        args.layers = args.layers > 0 ? args.layers : Math.floor(1 / args.thickness)
        let [centerParams, endParams] = this.getRandomizedEndParams(args)
        let curvePoints = this.getRandomPoints(endParams, args)
        let randomCurves = this.getRandomShapes(curvePoints, endParams, args)
        return [randomCurves, curvePoints]
    }
}

export { ShapeFillRandom }
